var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlert } from 'components/alert/AlertStack';
import { Confirm } from 'components/confirm/Confirm';
import { useModal } from 'components/modal/ModalStack';
import { DeleteEntityReminderMutation } from 'graphql/EntityReminder/mutations/DeleteEntityReminderMutation';
import { UpdateEntityReminderMutation } from 'graphql/EntityReminder/mutations/UpdateEntityReminderMutation';
import { formatDueDate, reminderDuePresets } from 'helpers/entityReminder';
import { React, useMutation, useState } from 'libs/react';
/**
 * @function useReminderActions
 * @since 1.0.0
 * Complete, postpone and delete, shared by every list of reminders. Menu
 * values are understood by `select`, except "edit" which the caller handles.
 */
export function useReminderActions(refetch) {
    const alert = useAlert();
    const modal = useModal();
    const [busyId, setBusyId] = useState(null);
    const [updateReminder] = useMutation(UpdateEntityReminderMutation);
    const [deleteReminder] = useMutation(DeleteEntityReminderMutation);
    /**
     * @function update
     * @since 1.0.0
     */
    function update(id, input) {
        return __awaiter(this, void 0, void 0, function* () {
            yield updateReminder({
                variables: {
                    input: Object.assign({ id }, input)
                }
            });
            refetch();
        });
    }
    /**
     * @function run
     * @since 1.0.0
     * Shows a spinner on the row and reports failures.
     */
    function run(reminder, task) {
        return __awaiter(this, void 0, void 0, function* () {
            setBusyId(reminder.id);
            try {
                yield task();
            }
            catch (error) {
                alert.present({ error });
            }
            finally {
                setBusyId(null);
            }
        });
    }
    /**
     * @function toggle
     * @since 1.0.0
     */
    function toggle(reminder) {
        return run(reminder, () => update(reminder.id, { completed: !reminder.completedAt }));
    }
    /**
     * @function postpone
     * @since 1.0.0
     */
    function postpone(reminder, date) {
        return run(reminder, () => __awaiter(this, void 0, void 0, function* () {
            yield update(reminder.id, { dueAt: date.toISOString() });
            alert.present({
                title: t('Reminder postponed to {date}.').replace('{date}', formatDueDate(date))
            });
        }));
    }
    /**
     * @function remove
     * @since 1.0.0
     */
    function remove(reminder) {
        function execute() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    yield deleteReminder({
                        variables: {
                            input: { id: reminder.id }
                        }
                    });
                    refetch();
                    alert.present({
                        title: t('Reminder deleted successfully.')
                    });
                }
                catch (error) {
                    alert.present({ error });
                }
            });
        }
        modal.present({
            style: 'dialog',
            title: t('Confirm'),
            picto: 'check',
            modal: (React.createElement(Confirm, { title: t('Delete this reminder?'), infos: t('This action cannot be undone.'), onAccept: execute }))
        });
    }
    /**
     * @function menuItems
     * @since 1.0.0
     */
    function menuItems(reminder, options = {}) {
        const items = [];
        if (options.edit) {
            items.push({ value: 'edit', label: t('Edit'), picto: 'fal pen' });
        }
        if (!reminder.completedAt) {
            items.push({ label: t('Postpone') });
            for (const preset of reminderDuePresets()) {
                items.push({
                    value: 'postpone:' + preset.key,
                    label: preset.label,
                    hint: formatDueDate(preset.date)
                });
            }
            // Untitled section: a plain divider before "Delete"
            items.push({ label: '' });
        }
        items.push({
            value: 'delete',
            label: t('Delete'),
            picto: 'fal trash',
            danger: true
        });
        return items;
    }
    /**
     * @function select
     * @since 1.0.0
     */
    function select(reminder, value) {
        if (value === 'delete') {
            remove(reminder);
            return;
        }
        const preset = reminderDuePresets().find((preset) => 'postpone:' + preset.key === value);
        if (preset) {
            postpone(reminder, preset.date);
        }
    }
    return { busyId, update, toggle, postpone, remove, menuItems, select };
}
